/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

// ============ 个人记忆类型 ============

export type MemoryStatus = 'active' | 'disabled' | 'expired';
export type MemoryGroup = 'basic' | 'preferences' | 'profile' | 'goals' | 'constraints';

export interface MemorySource {
  turn_id: number;
  snippet: string;
  redacted: boolean;
}

export interface MemoryItem {
  memory_id: string;
  title: string;
  value: any;
  confidence: number; // 0-1
  status: MemoryStatus;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  source: MemorySource;
}

export interface MemoryGroupData {
  group: MemoryGroup;
  items: MemoryItem[];
}

export interface PersonalMemoryResponse {
  settings: {
    memory_enabled: boolean;
  };
  groups: MemoryGroupData[];
  stats: {
    active_count: number;
    disabled_count: number;
    expired_count: number;
  };
}

// ============ 关系记忆类型 ============

export type EntityType = 'person' | 'org' | 'project';
export type RelationType =
  | 'coworker'
  | 'friend'
  | 'family'
  | 'customer'
  | 'mentor'
  | 'unknown';
export type TipType = 'style' | 'risk' | 'context';

export interface Entity {
  entity_id: string;
  entity_type: EntityType;
  display_name: string;
  aliases?: string[];
  last_seen_at: string;
}

export interface Edge {
  edge_id: string;
  from: 'user';
  to_entity_id: string;
  relation_type: RelationType;
  strength: number; // 0-1
  since?: string;
  last_interaction_at?: string;
  notes?: string;
}

export interface Fact {
  fact_id: string;
  entity_id: string;
  category: string;
  content: string;
  confidence: number; // 0-1
  status: MemoryStatus;
  expires_at: string | null;
  source: MemorySource;
}

export interface InteractionTip {
  type: TipType;
  text: string;
}

export interface EntityTips {
  entity_id: string;
  tips: InteractionTip[];
}

export interface RelationshipMemoryResponse {
  entities: Entity[];
  edges: Edge[];
  facts: Fact[];
  interaction_tips: EntityTips[];
}

// ============ 中文映射 ============

export const MEMORY_GROUP_LABELS: Record<MemoryGroup, string> = {
  basic: '基本信息',
  preferences: '偏好',
  profile: '背景',
  goals: '目标',
  constraints: '约束',
};

export const MEMORY_STATUS_LABELS: Record<MemoryStatus, string> = {
  active: '有效',
  disabled: '已禁用',
  expired: '已过期',
};

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  person: '人物',
  org: '组织',
  project: '项目',
};

export const RELATION_TYPE_LABELS: Record<RelationType, string> = {
  coworker: '同事',
  friend: '朋友',
  family: '家人',
  customer: '客户',
  mentor: '导师',
  unknown: '未知',
};

export const TIP_TYPE_LABELS: Record<TipType, string> = {
  style: '沟通风格',
  risk: '风险提示',
  context: '背景信息',
};
